import { Base2Presenter } from "core/presenter";
import {
  ProductRepository,
  StockOperationsRepository,
  UserRepository
} from "core/repository";
import { StockOperation } from "core/enum";
import Moment from "moment";
import * as FileSaver from "file-saver";

class StockMovementReportPresenter extends Base2Presenter {
  initialState() {
    const urlParams = new URLSearchParams(window.location.hash);
    const startDate = Moment(urlParams.get("inicio"), "DD-MM-YYYY");
    const endDate = Moment(urlParams.get("fim"), "DD-MM-YYYY");
    return {
      loading: true,
      filters: {
        dateFrom: startDate.isValid()
          ? startDate
          : Moment()
              .startOf("day")
              .subtract(7, "days"),
        dateTo: endDate.isValid() ? endDate : Moment().endOf("day"),
        operation: "all",
        user: {},
        product: {}
      },
      pagination: {
        page: 1,
        perPage: 25,
        total: 0
      },
      order: {
        field: "created_at",
        direction: "DESC"
      },
      productSearchOptions: {
        online: true,
        repository: ProductRepository,
        order: [{ field: "name", direction: "ASC" }],
        pagination: { page: 1, perPage: 25 },
        filter: query => this.getProductFilters(query)
      },
      userSearchOptions: {
        online: true,
        repository: UserRepository,
        order: [{ field: "name", direction: "ASC" }],
        pagination: { page: 1, perPage: 25 },
        filter: async query => await this.getUserFilters(query)
      },
      results: []
    };
  }

  async onStart() {
    this.load();
  }

  async load() {
    this.enqueue({ loading: true });
    await this.loadResults();
    this.enqueue({ loading: false });
  }

  async loadResults() {
    this.renewCancelToken();

    const filters = await this.getFilters();

    const { product } = this.viewModel.filters;
    let repositoryResult;

    if (product && product.uuid) {
      repositoryResult = await StockOperationsRepository.getListOnline({
        order: this.getOrder(),
        pagination: this.viewModel.pagination,
        filters
      });
    } else {
      repositoryResult = {
        success: true,
        data: [],
        total: 0,
        perPage: this.viewModel.pagination.perPage
      };
    }

    if (repositoryResult.success) {
      this.enqueue(vm => ({
        results: repositoryResult.data,
        pagination: {
          ...vm.pagination,
          total: repositoryResult.total,
          perPage: repositoryResult.perPage
        }
      }));
    } else if (!repositoryResult.userCancelled) {
      this.sendNotification({
        type: "error",
        title: "Oops",
        content:
          "Não foi possível carregar os produtos.\nTente novamente mais tarde ou entre em contato com o suporte."
      });
    }
  }

  async getFilters() {
    const {
      dateFrom,
      dateTo,
      operation,
      user,
      product
    } = this.viewModel.filters;

    const currentUser = await this.mRuntime.get("currentUser");

    const filters = [
      {
        connector: "AND",
        field: "company_uuid",
        operation: "EQ",
        value: currentUser.company_uuid
      }
    ];

    if (dateFrom) {
      filters.push({
        connector: "AND",
        field: "created_at",
        operation: "GTE",
        value: dateFrom.startOf("day").toISOString()
      });
    }

    if (dateTo) {
      filters.push({
        connector: "AND",
        field: "created_at",
        operation: "LTE",
        value: dateTo.endOf("day").toISOString()
      });
    }

    if (operation && operation !== "all") {
      filters.push({
        connector: "AND",
        field: "activity",
        operation: "EQ",
        value: operation
      });
    }

    if (user && user.uuid) {
      filters.push({
        connector: "AND",
        field: "user_uuid",
        operation: "EQ",
        value: user.uuid
      });
    }

    if (product && product.uuid) {
      filters.push({
        connector: "AND",
        field: "sellable_uuid",
        operation: "EQ",
        value: product.uuid
      });
    }

    return filters;
  }

  getProductFilters(query) {
    return [
      { connector: "OR", field: "code", operation: "LIKE", value: query },
      { connector: "OR", field: "name", operation: "LIKE", value: query },
      { connector: "AND", field: "deleted", operation: "NOT", value: "true" }
    ];
  }

  async getUserFilters(query) {
    const companyUuid = (await this.mRuntime.get("currentUser")).company_uuid;

    return [
      { connector: "OR", field: "name", operation: "LIKE", value: query },
      { connector: "AND", field: "deleted", operation: "NOT", value: "true" },
      {
        connector: "AND",
        field: "company_uuid",
        operation: "EQ",
        value: companyUuid
      }
    ];
  }

  async onDownload(format) {
    this.enqueue({ loading: true });

    switch (format) {
      case "csv":
        await this._downloadCSV();
        break;

      default:
        this.sendNotification({
          type: "error",
          title: "Oops...",
          content: "Ainda não suportamos esse formato"
        });
    }

    this.enqueue({ loading: false });
  }

  async _stockToReportCsvLine(stock) {
    const userRepositoryResults = await UserRepository.get(stock.user_uuid);
    const productRepositoryResults = await ProductRepository.get(
      stock.sellable_uuid
    );

    if (userRepositoryResults.success && productRepositoryResults.success) {
      return [
        Moment(stock.created_at).format("DD/MM/YYYY HH:mm:ss"),
        productRepositoryResults.data.name,
        stock.activity,
        stock.amount,
        stock.cost_value,
        userRepositoryResults.data.name
      ];
    }
  }

  async _downloadCSV() {
    let repositoryResult = {};
    const header = [
      "Data",
      "Produto",
      "Operação",
      "Quantidade Movimentada",
      "Saldo",
      "Usuário"
    ];

    repositoryResult = await StockOperationsRepository.getReportCsvContent(
      {
        order: this.getOrder(),
        filters: await this.getFilters(),
        callback: async model => await this._stockToReportCsvLine(model)
      },
      header,
      async stock => await this._stockToReportCsvLine(stock)
    );

    if (!repositoryResult.success) {
      this.sendNotification({
        type: "error",
        title: "Ooopss...",
        content:
          "Não foi possível gerar o download do seu arquivo. Por favor tente novamente mais tarde.\nSe o problema persistir, entre em contato com o suporte."
      });

      return;
    }

    const content = repositoryResult.data;
    const contentBlob = new Blob([content], {
      type: "application/csv;charset=utf-8"
    });
    FileSaver.saveAs(contentBlob, "movimentacao-de-estoque.csv");
  }

  onProductReports() {
    this.navigateTo("app.management.product-sales-report");
  }

  getOrder() {
    const { field, direction } = this.viewModel.order;
    const order = { direction };

    if (field === "user_operator.name") {
      order.mapFunction = async object => {
        const result = await UserRepository.get(object.user_uuid);

        if (!result.success) {
          return "";
        }

        return result.data.name
          .toLowerCase()
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "");
      };
    } else if (field === "product_name.name") {
      order.mapFunction = async object => {
        const result = await ProductRepository.get(object.sellable_uuid);

        if (!result.success) {
          return "";
        }

        return result.data.name
          .toLowerCase()
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "");
      };
    } else if (field === "activity") {
      order.mapFunction = async object => await StockOperation[object.activity];
    } else {
      order.field = field;
    }
    return [order];
  }

  onFilterChange(filters) {
    if (filters.dateFrom && filters.dateTo) {
      window.location.hash = `?&inicio=${Moment(filters.dateFrom).format(
        "DD-MM-YYYY"
      )}&fim=${Moment(filters.dateTo).format("DD-MM-YYYY")}`;
    }
    for (const key in filters) {
      if (this.viewModel.filters[key] !== filters[key]) {
        this.enqueue(
          vm => ({ filters: { ...vm.filters, ...filters } }),
          () => this.load()
        );
        break;
      }
    }
  }

  onChangeOrder(order) {
    this.enqueue({ order }, () => this.load());
  }

  onChangePage(page) {
    this.enqueue(
      vm => ({
        pagination: {
          ...vm.pagination,
          page
        }
      }),
      () => this.load()
    );
  }
}

export default StockMovementReportPresenter;



// WEBPACK FOOTER //
// ./src/components/app/management/stock-movement-report/presenter.js